<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */

    import Item from '../item/Item.svelte';
    import ResponsePanel from './ResponsePanel.svelte';
    import PreviewerHeaderBar from './PreviewerHeaderBar.svelte';
    import request from 'core/fetchRequest';
    import { getItemStateStore } from '../itemsStateStore.js';
    import prettyPrint from '../helpers/prettyPrint.js';

    let props = $props();

    // svelte-ignore state_referenced_locally
    let {
        jwtTokenHandler,
        options = { itemRunnerConfig: { previewerMode: { submitResponseUrl: '', itemUri: '' } } },
        itemIdentifier,
        itemTitle,
        itemRunner = null,
        content = null,
        onclose,
        onerror,
        onready
    } = props;

    //svelte binding to item component
    let itemComponent;
    let responses = $state([]);

    let showResponsePanel = $state(false);

    const { submitResponseUrl, itemUri, readOnly: previewerReadOnly = false } =
        options.itemRunnerConfig.previewerMode;

    const isReadOnly = previewerReadOnly || options.renderer === 'readonly';

    const stateStore = getItemStateStore(itemIdentifier);

    /**
     * Emits `error` event
     * @param {Object} e
     */
    function forwardErrorEvent(e) {
        onerror?.(e);
    }

    /**
     * Emits `close` event
     * @param {Object} e
     */
    function forwardCloseEvent(e) {
        onclose?.(e);
    }

    /**
     * Gets the responses object from itemComponent
     * @returns {object|void} item responses object
     */
    function getItemResponses() {
        if (itemComponent && itemComponent.trigger) {
            itemComponent.trigger('stateupdate');
        }
        if (stateStore) {
            return stateStore.getItemResponses();
        }
    }

    /**
     * Updates responses object used by responses panel with score data
     * @param {string} submitId
     * @param {object} scoreResponse submitItem response object.
     *  Response structures:
     * - scoring mode: outcomeDeclaration with SCORE and MAXSCORE
     * - no scoring: display 0 as the fallback
     */
    function updateResponses(submitId, scoreResponse) {
        const currentResponse = responses.find(response => response.submitId === submitId);
        currentResponse.score = getScore(scoreResponse, 'SCORE') || 0;
        currentResponse.maxScore = getScore(scoreResponse, 'MAXSCORE') || 0;
    }

    /**
     * Returns score value
     * @param {object} response response object
     * @param {string} scoreType SCORE / MAXSCORE
     * @returns {number|undefined} value of score
     */
    function getScore(response, scoreType) {
        let result;
        if (
            response &&
            response.itemSession &&
            response.itemSession[scoreType] &&
            response.itemSession[scoreType].base
        ) {
            result = response.itemSession[scoreType].base.float;
        }
        return result;
    }

    /**
     * Format response data for ResponsePanel
     * @param {object} itemResponses
     * @returns {object[]} array of responses
     */
    function formatItemResponses(itemResponses) {
        const responseIds = Object.keys(itemResponses);
        return responseIds.map(responseId => ({
            identifier: responseId,
            responseData: prettyPrint(itemResponses[responseId])
        }));
    }

    /**
     * Displays modal feedback if conditions are met
     * @param {object} response - submitItem response object
     */
    function displayFeedback(response) {
        if (
            response &&
            response.displayFeedback &&
            response.itemSession &&
            itemRunner &&
            typeof itemRunner.renderFeedbacks === 'function'
        ) {
            const feedbacks = content?.data?.feedbacks;

            if (feedbacks) {
                itemRunner.renderFeedbacks(feedbacks, response.itemSession);
            }
        }
    }

    /**
     * Sends post request to submitResponse endpoint
     */
    function submitItemResponse() {
        const submitId = Symbol('submitId');
        const itemResponses = getItemResponses();

        responses.push({
            submitId,
            itemResponses: formatItemResponses(itemResponses),
            score: null,
            maxScrore: null
        });

        let submitItemUrl = submitResponseUrl;
        try {
            submitItemUrl = new URL(submitResponseUrl);
            // searchParams are required for external previewer; not needed for internal but can be added harmlessly
            submitItemUrl.searchParams.append('serviceCallId', 'previewer');
            if (itemUri) {
                submitItemUrl.searchParams.append('itemUri', itemUri);
            }
        } catch (err) {
            console.error(err);
        }

        // eslint-disable-next-line svelte/prefer-svelte-reactivity
        const params = new URLSearchParams();
        params.set('itemResponse', JSON.stringify(itemResponses));

        const requestOptions = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8'
            },
            body: params,
            jwtTokenHandler
        };

        request(submitItemUrl, requestOptions).then(response => {
            updateResponses(submitId, response);
            displayFeedback(response);
        });
    }

    /**
     * Update the response panel visibility
     */
    function handleToggleResponsePanel() {
        showResponsePanel = !showResponsePanel;
    }
</script>

<style>
    .item-previewer-wrapper {
        height: 100%;
        width: 100%;
        display: flex;
        flex-direction: column;
    }

    .item-preview-header {
        height: 7rem;
        flex-grow: 0;
        flex-shrink: 0;
    }

    .item-preview-body {
        display: flex;
        flex-direction: row-reverse;
        flex-grow: 1;
        align-items: stretch;
        overflow: hidden;
    }

    .item-preview-container {
        flex-grow: 1;
        background-color: var(--color-bg-default);
        overflow-y: auto;
        overflow-x: hidden;
    }

    .qti-item-container {
        --testrunner-item-max-width: 170.75rem;
        max-width: var(--testrunner-item-max-width);
        margin-inline: auto;
        block-size: auto;
    }

    .response-panel-container {
        flex: 41.5rem 0 0;
    }

    .hidden {
        display: none;
    }

    @media only screen and (--mq-maxwidth-large) {
        .item-preview-body {
            flex-direction: column-reverse;
            align-items: stretch;
        }

        .item-preview-container {
            flex-grow: 1;
        }

        .response-panel-container {
            height: 39rem 0 0;
        }
    }
</style>

<div class="item-previewer-wrapper">
    {#if !isReadOnly}
        <div class="item-preview-header inverted">
            <PreviewerHeaderBar
                {showResponsePanel}
                {itemTitle}
                ontoggleResponsePanel={handleToggleResponsePanel}
                onclose={forwardCloseEvent} />
        </div>
    {/if}

    <div class="item-preview-body">
        {#if !isReadOnly}
            <div class="response-panel-container" class:hidden={!showResponsePanel}>
                <ResponsePanel {responses} onsubmit={submitItemResponse} />
            </div>
        {/if}
        <div class="item-preview-container">
            <div class="qti-item-container">
                <Item {...props} onerror={forwardErrorEvent} {onready} bind:this={itemComponent} />
            </div>
        </div>
    </div>
</div>
